type AddToCartModalWithUserInput<ModalName extends string, ResultType> = {
  name: ModalName;
  result: ResultType;
};

type AddToCartModalWithoutUserInput<ModalName extends string> = {
  name: ModalName;
};

/*
 * Add To Cart Modal Types.
 * Add To Cart Modals with user input will map there result type to
 * their submit modal callbacks.
 */
type Accessories = AddToCartModalWithUserInput<"accessories", string[]>;
type VirtualGrouping = AddToCartModalWithUserInput<
  "virtualGrouping",
  VirtualGroupingInput
>;
type Outlet = AddToCartModalWithoutUserInput<"outlet">;
type Warning = AddToCartModalWithUserInput<"warning", boolean>;
type CommercialUse = AddToCartModalWithUserInput<"commercialUse", boolean>;
type Warranty = AddToCartModalWithUserInput<"warranty", WarrantyOption>;

export type VirtualGroupingInput = {
  groupMemberItemNumber: string;
  hasAccessories: boolean;
};

export type WarrantyOption = {
  warrantyId: number;
  skipCommercialModal?: boolean;
  noThanksSelected?: boolean;
};

/*
 * Add all of the Modals from above to one of these unions.
 */
type AddToCartModalsWithUserInput =
  | Accessories
  | VirtualGrouping
  | Warranty
  | CommercialUse
  | Warning;
type AddToCartModalsWithoutUserInput = Outlet;

type AddToCartModals =
  | AddToCartModalsWithUserInput
  | AddToCartModalsWithoutUserInput;

export type AddToCartModalNames = AddToCartModals["name"];
type AddToCartModalWithUserInputName = AddToCartModalsWithUserInput["name"];

type UnboxProductModalUserInput<
  ModalName extends AddToCartModalWithUserInputName,
  BoxedProductResult extends AddToCartModalsWithUserInput,
> =
  BoxedProductResult extends AddToCartModalWithUserInput<
    ModalName,
    infer ModalResult
  >
    ? ModalResult
    : never;

/*
 * Takes the name of the modal and determines its result type
 */
type AddToCartModalUserInput<
  ModalName extends AddToCartModalWithUserInputName,
> = UnboxProductModalUserInput<ModalName, AddToCartModalsWithUserInput>;

export type AddToCartModal<ModalName extends AddToCartModalNames> = {
  isOpen: boolean;
  closeModal: () => void;
  submitModal: ModalName extends AddToCartModalWithUserInputName
    ? (result: { [K in ModalName]: AddToCartModalUserInput<ModalName> }) => void
    : () => void;
};

/*
 * Maps the modal name to a key with the correct type
 * EX:
 *  ProductModalWithResult<"isTrue", boolean> -> isTrue?: boolean | undefined;
 *  ProductModalWithResult<"testArray", string[]> -> testArray?: string[] | undefined;
 */
export type AddToCartModalUserInputs = {
  [Key in AddToCartModalWithUserInputName]?: AddToCartModalUserInput<Key>;
};

export type State = {
  modalsToOpen: AddToCartModalNames[];
  modalResults: AddToCartModalUserInputs;
  activeModal: AddToCartModalNames | "";
};

export type Action =
  | {
      type: "openModal";
      firstModal: AddToCartModalNames;
      otherModals: AddToCartModalNames[];
    }
  | {
      type: "submitModal";
      result?: AddToCartModalUserInputs;
      nextModal: AddToCartModalNames;
      otherModals: AddToCartModalNames[];
    }
  | {
      type: "reset";
    };

export const initialState: State = {
  modalsToOpen: [],
  modalResults: {},
  activeModal: "",
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "openModal":
      return {
        modalResults: {},
        activeModal: action.firstModal,
        modalsToOpen: action.otherModals,
      };
    case "submitModal":
      return {
        activeModal: action.nextModal,
        modalsToOpen: action.otherModals,
        modalResults: {
          ...state.modalResults,
          ...action.result,
        },
      };
    case "reset":
      return initialState;
    default:
      return state;
  }
};
